import { useMemo, useState } from 'react';

import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useUserSocialCirclesQuery } from '@queries/UserSocialCircleQueries';

import { createScreenComponent } from '@utilities/createScreenComponent';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserOnboardingGatePopup } from '@components/overlays/UserOnboardingGatePopup';

import { SocialCirclesContent } from '@screens/social-circles/SocialCirclesScreen/SocialCirclesScreen';

import { ConnectQrCodePopup } from './components/ConnectQrCodePopup';
import { ConnectionsContent } from './components/ConnectionsContent';
import { SuggestedConnectionsBannerLink } from './components/SuggestedConnectionsBannerLink';

enum TabOption {
  All = 'all',
  Circles = 'circles',
}

export const ConnectionsScreen = createScreenComponent<
  {},
  { openQrCodePopup?: 'true'; suggestedConnectionUserId?: string }
>(({ f7route }) => {
  const [currentTab, setCurrentTab] = useState<TabOption>(TabOption.All);
  const [qrCodeIsVisible, setQrCodeIsVisible] = useState(f7route.query?.openQrCodePopup === 'true');
  const { suggestedConnections = [] } = useUserConnectionsQuery();
  const { socialCircles } = useUserSocialCirclesQuery();

  const tabOptions = useMemo(() => {
    return [
      { value: TabOption.All, label: 'All' },
      { isVisible: !!socialCircles?.length, value: TabOption.Circles, label: 'Circles' },
    ].filter(({ isVisible = true }) => isVisible);
  }, [socialCircles?.length]);

  return (
    <Screen
      name="Connections Screen"
      headerProps={{
        left: <></>,
        title: <Screen.GagglTitle />,
      }}
    >
      <Screen.Content>
        <TitleToolbar
          text="Connections"
          action={
            <IconButton
              color="neutral"
              variant="smooth"
              onClick={() => setQrCodeIsVisible(true)}
              style={{ fontSize: '1.5rem', height: 48, width: 48 }}
            >
              <FontAwesomeIcon name="qrcode" />
            </IconButton>
          }
        />

        <SuggestedConnectionsBannerLink />

        {tabOptions.length > 1 && (
          <ButtonFilter
            className="mt-0"
            options={tabOptions}
            selected={currentTab}
            onChange={(value) => setCurrentTab(value as TabOption)}
          />
        )}
        {currentTab === TabOption.All && (
          <ConnectionsContent
            onClickConnect={() => setQrCodeIsVisible(true)}
            suggestedConnectionUserId={f7route.query?.suggestedConnectionUserId}
          />
        )}
        {currentTab === TabOption.Circles && <SocialCirclesContent />}
      </Screen.Content>
      <ConnectQrCodePopup isVisible={qrCodeIsVisible} onClose={() => setQrCodeIsVisible(false)} />
      <UserOnboardingGatePopup />
    </Screen>
  );
});
